import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Divider, Radio, Select, Space, Spin, Typography } from 'antd';
import { CheckCircleOutlined, CreditCardOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { usePricingApi, TPricing } from '../../../api/paiement/pricing';
import type { Periodicite } from '../../../api/paiement/paiement';
import { useReferential } from '../../../context/ReferentialContext';
import { signupPalette, signupGradient } from './signupTheme';

const { Text } = Typography;

/** Moyens proposés en fin de tunnel, alignés sur l'écran Paiement. */
export type PaymentMethod = 'cashplus' | 'lemonsqueezy';

type Props = {
    childrenCount: number;
    country?: string;
    onCountryChange: (code: string) => void;
    onPay: (params: {
        months: number;
        country: string;
        amountPerAccount: number;
        currency: string;
        /** Moyen retenu : le wizard appelle l'API correspondante. */
        method: PaymentMethod;
        /** Rythme de prélèvement — carte uniquement, un abonnement se reconduit. */
        periodicite?: Periodicite;
    }) => void;
    onSkip: () => void;
    submitting: boolean;
    error?: string;
};

/**
 * Étape 5 — paiement directement dans le tunnel.
 *
 * Le pays est demandé ici et nulle part ailleurs : c'est lui qui détermine le
 * tarif (table pricing par pays) et il est enregistré sur les comptes créés.
 */
const StepPayment: React.FC<Props> = ({
    childrenCount,
    country,
    onCountryChange,
    onPay,
    onSkip,
    submitting,
    error,
}) => {
    const { t } = useTranslation();
    const pricingApi = usePricingApi();
    const { countries, isReferentialLoaded } = useReferential();

    const [pricing, setPricing] = useState<TPricing[] | null>(null);
    const [pricingError, setPricingError] = useState(false);
    const [method, setMethod] = useState<PaymentMethod>('cashplus');

    const [mode, setMode] = useState<'months' | 'years'>('months');
    // Rythme de prelevement, uniquement pour la carte. Un abonnement se
    // reconduit jusqu'a resiliation : choisir « 2 mois » n'a aucun sens, et le
    // serveur prelevait alors 2 mois... chaque mois.
    const [periodicite, setPeriodicite] = useState<Periodicite>('mensuelle');
    const [months, setMonths] = useState(1);
    const [years, setYears] = useState(1);

    useEffect(() => {
        let cancelled = false;
        pricingApi
            .list()
            .then((items) => {
                if (!cancelled) setPricing(items || []);
            })
            .catch(() => {
                if (!cancelled) {
                    setPricing([]);
                    setPricingError(true);
                }
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const tariff = useMemo(() => {
        if (!pricing || !country) return undefined;
        return pricing.find((p) => p.codepays?.toUpperCase() === country.toUpperCase());
    }, [pricing, country]);

    // CashPlus encaisse une fois pour la duree choisie ; la carte preleve a
    // chaque echeance. Les deux ne comptent donc pas la meme chose.
    const moisParEcheance = periodicite === 'annuelle' ? 12 : periodicite === 'trimestrielle' ? 3 : 1;
    const totalMonths =
        method === 'lemonsqueezy'
            ? moisParEcheance
            : mode === 'years'
              ? Math.max(1, years) * 12
              : Math.max(1, months);

    // Le tunnel ne connait que le tarif public du pays : aucun code d'ecole n'y
    // est saisi. Le serveur (service.Tarifer) recalcule le montant de toute
    // facon ; l'ecran annonce le meme.
    const prixUnitaire = tariff?.prix ?? 0;
    const currency = tariff?.devise ?? '';

    const amountPerAccount = prixUnitaire * totalMonths;
    const totalAmount = amountPerAccount * childrenCount;
    const canPay = Boolean(country) && prixUnitaire > 0 && !submitting;

    const rythmeLabel =
        periodicite === 'annuelle'
            ? t('signup.payment.perYear', 'chaque année')
            : periodicite === 'trimestrielle'
              ? t('signup.payment.perQuarter', 'tous les 3 mois')
              : t('signup.payment.perMonth', 'chaque mois');

    const durationLabel = useMemo(() => {
        if (mode === 'years') return t('signup.payment.yearsCount', '{{count}} an', { count: Math.max(1, years) });
        return t('signup.payment.monthsCount', '{{count}} mois', { count: Math.max(1, months) });
    }, [mode, months, years, t]);

    return (
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
            {error && <Alert type="error" showIcon message={error} />}

            {/* Pays de facturation — détermine le tarif applicable */}
            <div>
                <Text style={{ fontWeight: 500, display: 'block', marginBottom: 8 }}>
                    {t('signup.payment.countryLabel', 'Pays de facturation')}
                </Text>
                <Select
                    showSearch
                    size="large"
                    style={{ width: '100%' }}
                    value={country || undefined}
                    onChange={onCountryChange}
                    loading={!isReferentialLoaded}
                    optionFilterProp="label"
                    placeholder={t('signup.payment.countryPlaceholder', 'Sélectionnez votre pays')}
                    options={countries.map((c) => ({ value: c.code, label: c.name }))}
                />
            </div>

            {/* Durée d'abonnement.

                Par CARTE, c'est un abonnement reconductible : on ne choisit pas
                une quantite de mois mais un RYTHME de prelevement. Proposer
                « 2 mois » laissait croire a un paiement unique, et le serveur
                prelevait alors ces 2 mois a chaque echeance.

                Par CASHPLUS, l'encaissement est unique : la duree choisie est
                bien la periode couverte. */}
            {method === 'lemonsqueezy' ? (
                <div>
                    <Text style={{ fontWeight: 500, display: 'block', marginBottom: 8 }}>
                        {t('signup.payment.rhythmLabel', 'Rythme de prélèvement')}
                    </Text>
                    <Radio.Group
                        value={periodicite}
                        onChange={(e) => setPeriodicite(e.target.value)}
                        optionType="button"
                        buttonStyle="solid"
                        style={{ display: 'flex', width: '100%' }}
                    >
                        <Radio.Button value="mensuelle" style={{ flex: 1, textAlign: 'center' }}>
                            {t('signup.payment.monthly', 'Mensuel')}
                        </Radio.Button>
                        <Radio.Button value="trimestrielle" style={{ flex: 1, textAlign: 'center' }}>
                            {t('signup.payment.quarterly', 'Trimestriel')}
                        </Radio.Button>
                        <Radio.Button value="annuelle" style={{ flex: 1, textAlign: 'center' }}>
                            {t('signup.payment.yearly', 'Annuel')}
                        </Radio.Button>
                    </Radio.Group>
                    <Text style={{ fontSize: 12, color: signupPalette.textSecondary, display: 'block', marginTop: 8 }}>
                        {t(
                            'signup.payment.rhythmHint',
                            'Vous êtes prélevé automatiquement à chaque échéance, jusqu’à résiliation.'
                        )}
                    </Text>
                </div>
            ) : (
                <div>
                    <Text style={{ fontWeight: 500, display: 'block', marginBottom: 8 }}>
                        {t('signup.payment.durationLabel', 'Durée')}
                    </Text>
                    <Radio.Group
                        value={mode}
                        onChange={(e) => setMode(e.target.value)}
                        optionType="button"
                        buttonStyle="solid"
                        style={{ display: 'flex', width: '100%', marginBottom: 16 }}
                    >
                        <Radio.Button value="months" style={{ flex: 1, textAlign: 'center' }}>
                            {t('signup.payment.monthly', 'Mensuel')}
                        </Radio.Button>
                        <Radio.Button value="years" style={{ flex: 1, textAlign: 'center' }}>
                            {t('signup.payment.yearly', 'Annuel')}
                        </Radio.Button>
                    </Radio.Group>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
                        <Button
                            shape="circle"
                            icon={<MinusOutlined />}
                            disabled={mode === 'months' ? months <= 1 : years <= 1}
                            onClick={() =>
                                mode === 'months' ? setMonths((m) => Math.max(1, m - 1)) : setYears((y) => Math.max(1, y - 1))
                            }
                        />
                        <Text style={{ fontSize: 20, fontWeight: 600, minWidth: 110, textAlign: 'center' }}>{durationLabel}</Text>
                        <Button
                            shape="circle"
                            icon={<PlusOutlined />}
                            disabled={mode === 'months' ? months >= 120 : years >= 10}
                            onClick={() =>
                                mode === 'months' ? setMonths((m) => Math.min(120, m + 1)) : setYears((y) => Math.min(10, y + 1))
                            }
                        />
                    </div>
                </div>
            )}

            <Divider style={{ margin: 0 }} />

            {/* Récapitulatif */}
            {pricing === null ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                    <Spin />
                </div>
            ) : (
                <div
                    style={{
                        borderRadius: 16,
                        border: `1px solid ${signupPalette.border}`,
                        background: signupPalette.background,
                        padding: 20,
                    }}
                >
                    <SummaryRow
                        label={t('signup.payment.accounts', '{{count}} compte enfant', { count: childrenCount })}
                        value={
                            tariff
                                ? `${amountPerAccount.toFixed(2)} ${currency}`
                                : t('signup.payment.priceUnavailable', 'Tarif indisponible')
                        }
                    />
                    <SummaryRow
                        label={
                            method === 'lemonsqueezy'
                                ? t('signup.payment.billing', 'Prélèvement')
                                : t('signup.payment.duration', 'Durée')
                        }
                        value={method === 'lemonsqueezy' ? rythmeLabel : durationLabel}
                    />
                    <Divider style={{ margin: '12px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <Text style={{ fontWeight: 600, fontSize: 15 }}>
                            {method === 'lemonsqueezy'
                                ? t('signup.payment.totalPerTerm', 'À chaque échéance')
                                : t('signup.payment.total', 'Total à payer')}
                        </Text>
                        <Text style={{ fontWeight: 700, fontSize: 22, color: signupPalette.primary }}>
                            {tariff ? `${totalAmount.toFixed(2)} ${currency}` : '—'}
                        </Text>
                    </div>
                </div>
            )}

            {pricingError && (
                <Alert
                    type="warning"
                    showIcon
                    message={t('signup.payment.pricingError', 'Les tarifs n’ont pas pu être chargés.')}
                    description={t(
                        'signup.payment.pricingErrorHint',
                        'Vous pouvez créer vos comptes maintenant et payer depuis votre espace.'
                    )}
                />
            )}
            {!pricingError && country && pricing !== null && !tariff && (
                <Alert
                    type="warning"
                    showIcon
                    message={t('signup.payment.noTariff', 'Aucun tarif n’est défini pour ce pays.')}
                    description={t(
                        'signup.payment.noTariffHint',
                        'Créez vos comptes maintenant : notre équipe vous contactera pour le règlement.'
                    )}
                />
            )}

            {/* Moyen de paiement */}
            <div>
                <Text style={{ fontWeight: 500, display: 'block', marginBottom: 8 }}>
                    {t('signup.payment.methodLabel', 'Moyen de paiement')}
                </Text>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <MethodCard
                        selected={method === 'cashplus'}
                        onSelect={() => setMethod('cashplus')}
                        icon={<img src="/images/Logo_CASHPLUS.png" alt="CashPlus" style={{ height: 28 }} />}
                        hint={t('signup.payment.cashplusHint', 'Un code vous sera remis, à présenter dans une agence CashPlus.')}
                    />
                    <MethodCard
                        selected={method === 'lemonsqueezy'}
                        onSelect={() => setMethod('lemonsqueezy')}
                        icon={<CreditCardOutlined style={{ fontSize: 22, color: signupPalette.primary }} />}
                        label={t('signup.payment.card', 'Carte bancaire')}
                        hint={t(
                            'signup.payment.cardHint',
                            "Abonnement reconductible : vous êtes prélevé à chaque échéance jusqu'à résiliation."
                        )}
                    />
                </Space>
            </div>

            <Button
                type="primary"
                size="large"
                block
                loading={submitting}
                disabled={!canPay}
                onClick={() =>
                    onPay({
                        months: totalMonths,
                        country: country as string,
                        amountPerAccount,
                        currency,
                        method,
                        periodicite: method === 'lemonsqueezy' ? periodicite : undefined,
                    })
                }
                style={{ height: 52, borderRadius: 12, border: 'none', background: signupGradient, fontWeight: 600, fontSize: 16 }}
            >
                {t('signup.payment.pay', 'Créer mes comptes et payer')}
            </Button>

            {/* Le pays reste requis même sans paiement immédiat : il conditionne
                le tarif applicable au moment où le parent réglera. */}
            <Button type="link" block onClick={onSkip} disabled={submitting || !country}>
                {t('signup.payment.payLater', 'Créer mes comptes et payer plus tard')}
            </Button>
        </Space>
    );
};

/** Carte de choix d'un moyen de paiement, reprenant le style du tunnel. */
const MethodCard: React.FC<{
    selected: boolean;
    onSelect: () => void;
    icon: React.ReactNode;
    label?: string;
    hint: string;
}> = ({ selected, onSelect, icon, label, hint }) => (
    <div
        role="radio"
        aria-checked={selected}
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => e.key === 'Enter' && onSelect()}
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 18px',
            borderRadius: 14,
            cursor: 'pointer',
            border: `2px solid ${selected ? signupPalette.primary : '#e2e8f0'}`,
            background: selected ? `${signupPalette.primary}0d` : '#ffffff',
            transition: 'all 0.2s',
        }}
    >
        {icon}
        <div style={{ flex: 1 }}>
            {label && (
                <Text style={{ display: 'block', fontWeight: 600, color: signupPalette.textPrimary }}>{label}</Text>
            )}
            <Text style={{ color: signupPalette.textSecondary, fontSize: 13 }}>{hint}</Text>
        </div>
        {selected && <CheckCircleOutlined style={{ color: signupPalette.primary, fontSize: 20 }} />}
    </div>
);

const SummaryRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <Text style={{ color: signupPalette.textSecondary }}>{label}</Text>
        <Text style={{ fontWeight: 600, color: signupPalette.textPrimary }}>{value}</Text>
    </div>
);

export default StepPayment;
